import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import { CarePartnerResponseDTO, CarePartnersService, CarePartnerUpdateDTO } from '../client'
import { CancelablePromise } from '../client/core/CancelablePromise'

export const useCarePartner = (carePartnerId: string) => {
    const [carePartner, setCarePartner] = useState<CarePartnerResponseDTO | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const { enqueueSnackbar } = useSnackbar()

    const fetchCarePartner = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response: CancelablePromise<CarePartnerResponseDTO> = CarePartnersService.getCarePartnerCarePartnersCarePartnerIdGet({
                carePartnerId
            })
            const data = await response
            setCarePartner(data)
        } catch (err) {
            setError('Failed to fetch care partner data')
            enqueueSnackbar('Failed to fetch care partner data', { variant: 'error' })
        } finally {
            setLoading(false)
        }
    }, [carePartnerId, enqueueSnackbar])

    useEffect(() => {
        fetchCarePartner()
    }, [fetchCarePartner])

    const updateCarePartnerStatus = useCallback(
        async (status: NonNullable<CarePartnerUpdateDTO['status']>) => {
            setLoading(true)
            setError(null)
            try {
                await CarePartnersService.updateCarePartnerStatusCarePartnersCarePartnerIdStatusPatch({
                    carePartnerId,
                    newStatus: status
                })
                // Refresh care partner data after status update
                await fetchCarePartner()
                enqueueSnackbar('Status updated successfully', { variant: 'success' })
            } catch (err) {
                setError('Failed to update care partner status')
                enqueueSnackbar(`Error updating care partner status: ${err}`, { variant: 'error' })
            } finally {
                setLoading(false)
            }
        },
        [carePartnerId, enqueueSnackbar, fetchCarePartner]
    )

    return { carePartner, loading, error, fetchCarePartner, updateCarePartnerStatus }
}
